import { GetServerSideProps, NextPage } from "next";
import {
    dehydrate,
    DehydratedState,
    QueryClient,
    useQuery,
} from "@tanstack/react-query";

import { OpenGraph } from "next-seo/lib/types";
import { ItalianStoryResponseTo } from "@/models/italianStory";
import { isValidMongoId } from "@/utils/common";
import Seo from "@/components/common/Seo";
import Head from "next/head";
import { withRedirect } from "@/utils/redirects";
import {
    getBrochureData,
    getItalianStoryData,
    getNavBarData,
} from "../../utils/queries";
import ItalianStoryBody from "../../components/italianStories/italianStory/ItalianStoryBody";

const getOpenGraphData = (
    italianStory: ItalianStoryResponseTo | undefined
): OpenGraph => {
    if (italianStory) {
        return {
            title: italianStory.metadata?.title,
            description: italianStory.metadata?.metaDescription,
            url: italianStory.metadata?.urlCanonical,
            images: [
                ...(italianStory.stories?.map(story => {
                    return { url: story.image || "", alt: story.alt || "" };
                }) || []),
                {
                    url: italianStory.coverImage,
                    alt: italianStory?.metadata?.altHeadImage,
                },
            ],
            article: {
                publishedTime: italianStory.date || "",
                authors: [italianStory.author],
                tags:
                    italianStory.tags?.map(tag => {
                        return tag.label;
                    }) || [],
            },
        };
    }
    return {};
};

const ItalianStoryPage: NextPage<{ title: string }> = ({ title }) => {
    // se l'input dell'url è un numero ricerchiamo per id, altrimenti per titolo
    const navigationById = isValidMongoId(title);

    const { data: italianStoryData } = useQuery({
        queryKey: ["italianStoryData", title, navigationById],
        queryFn: () => getItalianStoryData(title, "en", navigationById),
        refetchInterval: false,
    });

    const metaTags = getOpenGraphData(italianStoryData);

    return (
        <>
            <Head>
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(
                            italianStoryData?.metadata?.structuredData ?? ""
                        ),
                    }}
                />
            </Head>
            <Seo
                seoInfo={metaTags!}
                noindex={!italianStoryData?.metadata?.index}
            />
            <div className="col">
                <ItalianStoryBody italianStory={italianStoryData || {}} />
            </div>
        </>
    );
};

export default ItalianStoryPage;

export const getServerSideProps: GetServerSideProps = withRedirect(
    async (
        ctx
    ): Promise<{
        props: {
            dehydratedState: DehydratedState;
            title: string;
            navigationById: boolean;
        };
    }> => {
        const queryClient = new QueryClient();

        const title =
            typeof ctx.params!.slug === "string"
                ? ctx.params!.slug
                : ctx.params!.slug?.at(0);

        // se l'input dell'url è un numero ricerchiamo per id, altrimenti per titolo
        const navigationById = isValidMongoId(title!);

        await Promise.all([
            await queryClient.prefetchQuery(["navBar"], getNavBarData),
            await queryClient.prefetchQuery(["brochures"], getBrochureData),
            await queryClient.prefetchQuery(
                ["italianStoryData", title, navigationById],
                () => getItalianStoryData(title!, "en", navigationById)
            ),
        ]);

        return {
            props: {
                dehydratedState: dehydrate(queryClient),
                title: title!,
                navigationById,
            },
        };
    }
);
